// Registers the service worker (production builds only) and surfaces updates to
// the rest of the app. Dispatches a window-level `app-update-available` CustomEvent
// whose detail is the ServiceWorkerRegistration. Listeners can call
// `applyUpdate(detail)` to activate the waiting worker — that triggers
// `controllerchange`, which reloads the page.
//
// Why no auto-skipWaiting? A POS may be mid-sale; we wait for an explicit user
// click in the UI banner.
//
// Update-check strategy:
//
//   1. `visibilitychange` → `visible`. The primary trigger. Catches the most
//      common case: operator unlocks the device or switches back from another
//      app, and we want the freshest code immediately.
//   2. Initial load. Runs at SW registration time.
//   3. Periodic safety poll, 15 min, gated on document.visibilityState ===
//      'visible'. Covers the "wall-mounted display that's never backgrounded"
//      case. Hidden tabs skip the check — no point burning network on a tab
//      no one is looking at.
//   4. Manual check via `checkForUpdate()` (About page exposes a button).
//
// Each check fetches /sw.js (~10 KB) from the SWA CDN; it does NOT hit the
// Function App backend. Bandwidth cost is negligible.

const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export type AppUpdateDetail = ServiceWorkerRegistration;

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  // Don't register a SW in dev. vite-plugin-pwa is configured with
  // devOptions.enabled: false; this guard is the belt-and-braces equivalent so a
  // stale dev-mode SW from a previous setup can't keep this code path alive.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', {
          // Force the browser to re-fetch sw.js itself (rather than serving the
          // browser's 24h-cached copy). Update detection depends on this.
          updateViaCache: 'none',
        });

        // If there's already a waiting worker on first load (e.g., user closed tab
        // before applying a previous update), surface it immediately.
        if (reg.waiting && navigator.serviceWorker.controller) {
          notifyUpdateAvailable(reg);
        }

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              notifyUpdateAvailable(reg);
            }
          });
        });

        // Periodic safety probe. Only runs while the document is
        // visible — a backgrounded tab burns no network. Primary cadence
        // is the visibility-change listener below; this poll just
        // catches the "screen never backgrounds" case (kitchen display,
        // wall-mounted tablet).
        setInterval(() => {
          if (document.visibilityState !== 'visible') return;
          reg.update().catch(() => {
            /* offline; try again next interval */
          });
        }, UPDATE_CHECK_INTERVAL_MS);

        // Primary update trigger. Fires on app resume — the moment the
        // operator's about to look at fresh data anyway.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            reg.update().catch(() => {});
          }
        });
      } catch (err) {
        console.warn('Service worker registration failed', err);
      }
    })();
  });

  // The new SW has taken over — reload so the user runs the new code.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

function notifyUpdateAvailable(reg: ServiceWorkerRegistration) {
  window.dispatchEvent(
    new CustomEvent<AppUpdateDetail>('app-update-available', { detail: reg })
  );
}

// Called by the UI when the user clicks "Reload" on the update prompt.
export function applyUpdate(reg: ServiceWorkerRegistration): void {
  const waiting = reg.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

// Result of a manual update check, with enough detail for the UI to
// show "you're up to date" vs "update available — reload to apply".
export type UpdateCheckResult =
  | { kind: 'up-to-date' }
  | { kind: 'update-available'; registration: ServiceWorkerRegistration }
  | { kind: 'no-service-worker' }
  | { kind: 'error'; message: string };

// Nuclear-option recovery when the SW + cache are in a bad state
// (stuck on a previous version, can't self-update, app shows
// "offline" or fails to boot). Unregisters the active SW, deletes
// every cache the browser holds for this origin, then reloads.
// Next launch boots through the network from scratch and installs
// a fresh SW.
//
// Used by the version-skew banner — when the boot detects that the
// cached client version is older than what the live server reports,
// we know the existing SW failed to update and a regular reload
// won't help. Unregister + cache-purge + reload IS the fix.
export async function hardResetAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.unregister().catch(() => {
          /* best effort */
        });
      }
    }
    if ('caches' in window) {
      const keys = await caches.keys().catch(() => [] as string[]);
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } finally {
    window.location.reload();
  }
}

// Manual check triggered from the About page. Forces a fresh fetch of
// sw.js and inspects the registration for an installing / waiting
// worker afterwards. Returns within a few seconds.
export async function checkForUpdate(): Promise<UpdateCheckResult> {
  if (!('serviceWorker' in navigator)) {
    return { kind: 'no-service-worker' };
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { kind: 'no-service-worker' };
    await reg.update();
    if (reg.waiting || reg.installing) {
      return { kind: 'update-available', registration: reg };
    }
    return { kind: 'up-to-date' };
  } catch (err) {
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
